import type { ExpressionToken, ParsedToken } from '../../../types/QueryBuilder';

/** Replace a token's span in the raw query string via character-offset splicing. */
export function replaceToken(query: string, token: ParsedToken, replacement: string): string {
  return query.substring(0, token.location.start.offset) + replacement + query.substring(token.location.end.offset);
}

/** Remove only the enclosing parentheses of an expression group, keeping the inner content. */
export function unwrapExpression(query: string, expression: ExpressionToken): string {
  // Skip the optional `<expressionType>(` prefix and the trailing `)` so a typed
  // group like `anySpan2(span_name:=:foo)` unwraps to `span_name:=:foo` instead of
  // chopping just the first and last character of the prefix.
  const openOffset = expression.location.start.offset + (expression.expressionType?.length ?? 0) + 1;
  const closeOffset = expression.location.end.offset - 1;
  const inner = query.substring(openOffset, closeOffset).trim();
  return replaceToken(query, expression, inner).replace(/\s+/g, ' ').trim();
}

/** Remove a token from the raw query string and normalize whitespace. */
export function removeToken(query: string, token: ParsedToken): string {
  const before = query.substring(0, token.location.start.offset);
  const after = query.substring(token.location.end.offset);
  return (before + after).replace(/\s+/g, ' ').trim();
}

/** Replace the contiguous span covered by `tokens` with `replacement`. */
export function replaceSelection(query: string, tokens: ParsedToken[], replacement: string): string {
  if (tokens.length === 0) {
    return query;
  }
  const sorted = [...tokens].sort((a, b) => a.location.start.offset - b.location.start.offset);
  const rangeStart = sorted[0].location.start.offset;
  const rangeEnd = sorted[sorted.length - 1].location.end.offset;
  return (query.substring(0, rangeStart) + replacement + query.substring(rangeEnd)).replace(/\s+/g, ' ').trim();
}

/**
 * Wrap a field name or value in double-quotes if it contains characters that are
 * structural delimiters in the query grammar (whitespace, colon, or comma).
 * An empty value is always serialized as "" (the grammar's representation of empty).
 */
export function quoteIfNeeded(text: string): string {
  // Fix: a `"` typed inside a value can't be stored — it would break the query — so drop any
  // quotes first, then only wrap the value in quotes if it has a space, colon, or comma.
  const stripped = text.replaceAll('"', '');
  if (stripped === '') {
    return '""';
  }
  if (/[\s:,()]/.test(stripped)) {
    return `"${stripped}"`;
  }
  return stripped;
}

/** FILTER values as text; `rawValues` is only a fallback for tokens built without `value`. */
export function tokenValueTexts(token: ParsedToken): string[] {
  if (token.type !== 'FILTER') {
    return [];
  }
  // `value` is typed as always present, but synthetic tokens can be built without one, so the
  // fallback is read up front — the nullish check below narrows the token itself away.
  const fallback = token.rawValues ?? [];
  const { value } = token;
  // An empty array is an explicit "no values selected" (the user deselected the last
  // option), not a missing `value`. Falling back to `rawValues` here would resurrect the
  // values the token was parsed from, making the final selection impossible to clear.
  if (Array.isArray(value)) {
    return value.map(String);
  }
  if (value !== undefined && value !== null) {
    return [String(value)];
  }
  return fallback;
}

/** Bare field names the grammar accepts unquoted; anything else has to be quoted to round-trip. */
const BARE_FIELD_RE = /^[a-zA-Z0-9_-]+(?:\.[a-zA-Z0-9_-]+)*$/;

/** Quote a field unless the grammar accepts it bare (`*.email` only parses quoted). */
export function quoteFieldIfNeeded(field: string): string {
  const stripped = field.replaceAll('"', '');
  return BARE_FIELD_RE.test(stripped) ? stripped : `"${stripped}"`;
}

/**
 * Serialize a parsed token back to its raw string form.
 * Used when mutating semantic fields (e.g. changing operator) rather than splicing .text directly.
 */
export function stringifyToken(token: ParsedToken): string {
  switch (token.type) {
    case 'FILTER': {
      const prefix = token.negated ? '!' : '';
      const field = quoteFieldIfNeeded(token.field);
      // An empty array joins to '', producing `field:op:` (no value) which FILTER_RE
      // rejects and re-parses as FREE_TEXT; emit `""` so the FILTER round-trips.
      const texts = tokenValueTexts(token);
      const valueStr = texts.length > 0 ? texts.map((text) => quoteIfNeeded(text)).join(',') : '""';
      return `${prefix}${field}:${token.operator}:${valueStr}`;
    }
    case 'FREE_TEXT':
      return token.value.includes(' ') ? `"${token.value}"` : token.value;
    case 'LOGIC_BOOL':
      return token.value;
    case 'LOGIC_NOT':
      return token.value;
    case 'EXPRESSION':
      return `${token.expressionType ?? ''}(${token.inner.map(stringifyToken).join(' ')})`;
    default:
      throw new Error(`Unsupported token type: ${(token as ParsedToken).type}`);
  }
}
